import { ReportParameterTypeEnum } from "@/Enums/ReportParameterType";
import { usePopups } from "@/Hooks/usePopups";
import { GetMyReportsExecutionHistoryDto } from "@/Schemas/Reports/GetMyReportsExecutionHistoryDto";
import { GetMyReportsExecutionHistoryInput } from "@/Schemas/Reports/GetMyReportsExecutionHistoryInput";
import { GetMyReportsExecutionHistoryOutput } from "@/Schemas/Reports/GetMyReportsExecutionHistoryOutput";
import { Box, CircularProgress, Icon, IconButton, LinearProgress, Tooltip, Typography, useTheme } from "@mui/material";
import { IoMdCheckmark, IoMdInformationCircleOutline } from "react-icons/io";
import { IoCloudDownloadOutline, IoServerOutline } from "react-icons/io5";
import { Column, Pagination, TextOverflow, TextOverflowWithIcon, UseTaskReturn, capitalizeSentences, formatDate, getYCenterStyles } from "vig-ui-library";
import { DataTable, PinnedColumn } from "vig-ui-library/src/components/dataTable/DataTable";
import { TabType } from "./RunnedAssembly";
import { MdError } from "react-icons/md";
import { formatRelativeTime } from "vig-ui-library/src/utils/date";

export type MyAssembliesInboxProps = {
    refreshExecutionHistoryTask: UseTaskReturn<[], GetMyReportsExecutionHistoryOutput>;
    query: GetMyReportsExecutionHistoryInput;
    setQuery: React.Dispatch<React.SetStateAction<GetMyReportsExecutionHistoryInput>>;
}

export const MyAssembliesInbox = ({ refreshExecutionHistoryTask, query, setQuery }: MyAssembliesInboxProps) => {
    const { openRunnedAssembly } = usePopups();

    const theme = useTheme();

    const nameColumn: Column<GetMyReportsExecutionHistoryDto> = {
        name: "Názov",
        width: 300,
        tableCellBuilder: (row) => {
            return <TextOverflowWithIcon
                text={row.report.name}
                maxWidth={300}

                icon={<Icon sx={{ fontSize: 24, color: theme.palette.common.black }}>{row.report.icon}</Icon>}
            />;
        }
    }

    const statusColumn: Column<GetMyReportsExecutionHistoryDto> = {
        name: "Stav",
        width: 200,
        tableCellBuilder: (row) => {
            if (row.errorMessage) {
                return <TextOverflowWithIcon
                    text="Nastala chyba"
                    icon={<MdError color={theme.palette.error.main} fontSize={24} />}
                    color={theme.palette.error.main}
                />;
            }

            if (!row.executionEndTime || !row.executionStartTime) {
                let label = "Spuštá sa...";
                if (row.executionStartTime) label = "Prebieha spracovanie...";
                return <Box sx={{ ...getYCenterStyles(), gap: 1 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" whiteSpace="nowrap">
                        {label}
                    </Typography>
                </Box>
            }

            return <TextOverflowWithIcon
                color={theme.palette.success.main}
                icon={<IoMdCheckmark color={theme.palette.success.main} fontSize={24} />}
                text="Spracovanie ukončené"
            />;
        }
    }

    const dateOfRunColumn: Column<GetMyReportsExecutionHistoryDto> = {
        name: "Dátum Spustenia",
        width: 200,
        tableCellBuilder: (row) => {
            if (!row.executionStartTime) return <TextOverflow text="Zostava ešte nebola spustená" />
            return <TextOverflow text={formatRelativeTime(row.executionStartTime)} />
        }
    }

    const getParametersText = (row: GetMyReportsExecutionHistoryDto) => {
        return row.reportExecutionHistoryParameters.map((parameter) => {
            const { name, type } = parameter.reportParameter || {};

            const values = parameter.values.map((obj) => {
                if (type === ReportParameterTypeEnum.DATE) return formatDate(obj.value);
                return obj.value;
            });

            const valueText = values.join(", ") || "Prázdne";

            return `${capitalizeSentences(name ?? "Neznámy parameter")}: ${valueText}`;
        }).join("\n");
    }

    const parametersColumn: Column<GetMyReportsExecutionHistoryDto> = {
        name: "Parametre",
        width: 380,
        cellTooltipBuilder: getParametersText,
        tableCellBuilder: (row) => {
            const text = getParametersText(row);
            return <TextOverflow text={text} width="380px" />;
        }
    }

    const optionsColumn: PinnedColumn<GetMyReportsExecutionHistoryDto> = {
        name: "",
        pinned: true,
        width: 150,
        tableCellBuilder: (row) => {
            return <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                <Tooltip title={row.errorMessage ? "Pozrieť chybu" : "Detail zostavy"}>
                    <IconButton
                        onClick={() => openRunnedAssembly(row, TabType.DETAIL)}
                        color="primary"
                    >
                        <IoMdInformationCircleOutline size={24} />
                    </IconButton>
                </Tooltip>

                {!row.errorMessage && (
                    <>
                        <Tooltip title="Dáta spustenej zostavy">
                            <IconButton onClick={() => openRunnedAssembly(row, TabType.DATA)} color="primary">
                                <IoServerOutline size={24} />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Súbory na stiahnutie">
                            <IconButton onClick={() => openRunnedAssembly(row, TabType.FILES_TO_DOWNLOAD)} color="primary">
                                <IoCloudDownloadOutline size={24} />
                            </IconButton>
                        </Tooltip>
                    </>
                )}
            </Box>;
        }
    }

    const columns: Column<GetMyReportsExecutionHistoryDto>[] = [
        nameColumn,
        statusColumn,
        dateOfRunColumn,
        parametersColumn,
        optionsColumn,
    ];

    return (
        <Box sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            height: "100%",
            width: "100%",
            flexGrow: 1,
            flexDirection: "column",
            overflowX: "hidden",
        }}>
            <LinearProgress sx={{
                visibility: !refreshExecutionHistoryTask.isInitialCall && refreshExecutionHistoryTask.isRunning ? "visible" : "hidden",
                width: "100%",
            }} />

            <Box sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                width: "100%",
                gap: 1,
                padding: 2,
            }}>
                <Typography variant="h6">Moja Schránka zostáv</Typography>
                <Typography variant="body2">
                    V tejto sekcií máte exporty ktoré ste spustili v minulosti a môžete si ich znova stiahnúť.
                    Exporty sú k dispozícií vo vašej schránke po dobu jedného mesiaca.
                </Typography>
            </Box>

            <Box display="flex" width="100%" pb={{ sm: 1, md: 2 }} p={2} justifyContent="flex-end">
                <Pagination
                    totalCount={refreshExecutionHistoryTask.value?.count || 0}
                    pageSizeOptions={[10, 25]}
                    page={query.page}
                    pageSize={query.limit ?? 10}
                    onChange={(page: number, limit: number) => {
                        setQuery({ ...query, page, limit });
                    }}
                />
            </Box>

            <Box sx={{ width: "100%", flexGrow: 1, overflowX: "hidden" }}>
                <DataTable
                    columns={columns}
                    getDataTask={refreshExecutionHistoryTask}
                    tableMinWidth={920}
                    query={query}
                    setQuery={setQuery}
                    paddingLeft={2}
                    showLoaderOnlyOnInitialCall
                    onRowDoubleClick={(row: GetMyReportsExecutionHistoryDto) => {
                        openRunnedAssembly(row);
                    }}
                />
            </Box>
        </Box>
    );
}